/**
 * ConsentEngine — Central state machine for Pritect Beacon.
 *
 * Coordinates: geo detection, storage, script blocking, banner rendering,
 * consent recording, and Google Consent Mode v2.
 */

import { StorageAdapter, type ConsentDecisions } from "./StorageAdapter";
import { GeoDetector, type GeoResult } from "./GeoDetector";
import { ScriptBlocker, type ConsentCategory, type BlockPattern } from "../blocking/ScriptBlocker";
import { BannerRenderer } from "../ui/BannerRenderer";
import { PreferenceCenter } from "../ui/PreferenceCenter";
import { GoogleConsentMode } from "../gcm/GoogleConsentMode";
import { ConsentClient } from "../api/ConsentClient";
import { ExperimentClient, type ExperimentVariant } from "../api/ExperimentClient";
import type { BannerConfig } from "../ui/types";
import { PolicyEvaluator } from "./PolicyEvaluator";
import { TcfApi } from "../tcf/TcfApi";
import { VendorListLoader } from "../tcf/VendorListLoader";
import type { VendorList } from "../tcf/TcfTypes";

export interface ConsentEngineOptions {
  domainId: string;
  apiUrl: string; // Supabase project URL
  anonKey: string; // Supabase public anon key
  lang?: string;
  /**
   * A ScriptBlocker that is already intercepting, handed over by the loader.
   *
   * The loader runs before every other tag on the page and starts blocking
   * there, because by the time this bundle arrives the parser has moved on.
   * Constructing a second blocker would install a second set of DOM hooks and
   * lose track of everything the first one already rewrote, so the running one
   * is adopted instead. Absent when the bundle is embedded directly, which is
   * what version-pinned snippets do.
   */
  blocker?: ScriptBlocker;
}

export class ConsentEngine {
  private options: ConsentEngineOptions;
  private storage: StorageAdapter;
  private geo: GeoDetector;
  private blocker: ScriptBlocker;
  /** True when the blocker came from the loader and is already intercepting. */
  private blockerAdopted: boolean;
  private gcm: GoogleConsentMode | null = null;
  private client: ConsentClient;
  private experiments: ExperimentClient;
  private activeExperiment: { id: string; variant: ExperimentVariant } | null = null;
  private sessionId: string;
  private evaluator: PolicyEvaluator = new PolicyEvaluator();
  private preferenceCenter: PreferenceCenter | null = null;
  private currentDecisions: ConsentDecisions | null = null;
  private currentConfig: BannerConfig | null = null;
  private currentGeo: GeoResult | null = null;
  private tcfApi: TcfApi | null = null;
  private vendorListLoader: VendorListLoader | null = null;
  private vendorList: VendorList | null = null;

  constructor(options: ConsentEngineOptions) {
    this.options = options;
    this.storage = new StorageAdapter(options.domainId);
    this.geo = new GeoDetector(options.apiUrl);
    this.blockerAdopted = !!options.blocker;
    this.blocker = options.blocker ?? new ScriptBlocker();
    this.client = new ConsentClient(options.apiUrl, options.domainId);
    this.experiments = new ExperimentClient(options.apiUrl, options.anonKey, options.domainId);
    this.sessionId = StorageAdapter.generateSessionId();
  }

  async init(): Promise<void> {
    // Step 1: Block scripts immediately (before awaiting anything). When the
    // loader handed one over it has been intercepting since before any other
    // tag on the page ran, so re-intercepting would only duplicate DOM hooks.
    if (!this.blockerAdopted) this.blocker.intercept();
    // Merge the per-domain scan-derived blocklist when it arrives so trackers
    // the scanner discovered are blocked, not just declared (best effort).
    void this.loadBlocklist();

    // Step 2: Detect geo & applicable regulation (needed for TCF decision)
    const geoResult = await this.geo.detect();

    // Step 2a: Check for existing valid consent
    const existing = this.storage.load();
    const CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000; // 12 months
    const isExpired = existing?.timestamp
      ? Date.now() - new Date(existing.timestamp).getTime() > CONSENT_MAX_AGE_MS
      : false;
    if (existing && !isExpired) {
      const fetched = await this.fetchBannerConfig();
      // A missing published config must not strand a returning visitor with no
      // way back to their choices: honour the stored decisions against the
      // fallback config so the preference center still renders. pb:error keeps
      // the degraded state observable.
      const config = fetched ?? this.fallbackBannerConfig();
      if (!fetched) {
        this.reportConfigurationError("No published banner config available; applying stored consent with the default banner config.");
      }

      // Re-apply consent from storage without showing banner
      this.currentDecisions = existing.decisions;
      this.currentGeo = geoResult;
      this.currentConfig = config;
      // Install TCF only when the customer enabled it and a real CMP id is set,
      // so we never broadcast an invalid (CMP id 0) TC string.
      if (geoResult.regulation === "gdpr" && config.tcf_enabled && (config.tcf_cmp_id ?? 0) > 0) {
        this.initTcf(true, existing.decisions, geoResult, config.tcf_cmp_id as number);
      }
      this.applyDecisions(existing.decisions, config);

      this.ensurePreferenceCenter(config, geoResult);
      return;
    }

    // If this region doesn't require consent, grant all and continue
    if (!geoResult.requiresConsent) {
      const allGranted: ConsentDecisions = {
        strictly_necessary: true,
        analytics: true,
        advertising: true,
        functional: true,
        social: true,
      };
      this.applyDecisions(allGranted, null);
      return;
    }

    // Step 4: Fetch banner config
    let config = await this.fetchBannerConfig();

    if (!config) {
      // Rendering nothing is not a safe failure for a consent tool: it leaves
      // the site with no consent UI at all while trackers stay blocked and no
      // visitor can ever unblock them. Fall back to a conservative default and
      // keep pb:error observable for operators.
      this.reportConfigurationError("No published banner config available; falling back to the default consent banner.");
      config = this.fallbackBannerConfig();
    }

    // Step 3b: The visitor dismissed the banner earlier this session (Escape,
    // close, overlay). Dismissal is not a decision: nothing was persisted, the
    // blocker stays engaged and GCM stays denied, and we just don't re-nag
    // within the session. Consent remains reachable via the preference center.
    if (this.isSoftDismissed()) {
      if (geoResult.regulation === "gdpr" && config.tcf_enabled && (config.tcf_cmp_id ?? 0) > 0) {
        this.initTcf(false, null, geoResult, config.tcf_cmp_id as number);
      }
      this.gcm = new GoogleConsentMode(config.gcm_enabled);
      this.gcm.setDefaults();
      this.currentConfig = config;
      this.currentGeo = geoResult;
      this.ensurePreferenceCenter(config, geoResult);
      return;
    }

    // Step 4-ab: A/B experiment. Assign a sticky variant and merge its config
    // overrides over the base banner config, so the visitor actually sees the
    // variant and the outcome can be measured. Only on the fresh-banner path.
    const experiment = await this.experiments.fetchActive();
    if (experiment) {
      const variant = ExperimentClient.assignVariant(
        this.storage.getVisitorId(),
        experiment.id,
        experiment.traffic_split
      );
      const overrides = (variant === "a" ? experiment.variant_a : experiment.variant_b) as Partial<BannerConfig>;
      config = { ...config, ...overrides };
      this.activeExperiment = { id: experiment.id, variant };
    }

    // Step 4a: Set up TCF only if GDPR applies, the customer enabled TCF, and a
    // real CMP id is configured. Installing it unconditionally with CMP id 0
    // produced invalid TC strings for every GDPR visitor.
    if (geoResult.regulation === "gdpr" && config.tcf_enabled && (config.tcf_cmp_id ?? 0) > 0) {
      this.initTcf(false, null, geoResult, config.tcf_cmp_id as number);
    }

    // Step 5: Set up GCM defaults before showing banner
    this.gcm = new GoogleConsentMode(config.gcm_enabled);
    this.gcm.setDefaults();

    // Step 6: Show banner
    this.currentConfig = config;
    this.currentGeo = geoResult;
    const renderer = new BannerRenderer(
      config,
      geoResult,
      (decisions) => this.handleDecision(decisions, config!),
      this.options.lang || navigator.language.split("-")[0] || "en",
      null,
      null,
      null,
      () => {
        // Dismissal (Escape/close/overlay): record nothing. Remember it for
        // this session only, and keep the preference center reachable.
        this.markSoftDismissed();
        this.ensurePreferenceCenter(config!, geoResult);
      }
    );
    renderer.show();

    // Record the experiment impression once the banner is on screen.
    if (this.activeExperiment) {
      this.experiments.record(this.activeExperiment.id, this.activeExperiment.variant, "impression");
    }
  }

  /**
   * Fetch the per-domain blocking manifest built from scan results and merge
   * it into the blocker, closing the scan-to-block loop. Served by the public,
   * cached consent-blocklist edge function (service-role read, so tracking data
   * is not exposed via an anon PostgREST policy). Best effort.
   */
  private async loadBlocklist(): Promise<void> {
    try {
      const res = await fetch(
        `${this.options.apiUrl}/functions/v1/consent-blocklist?domain_id=${this.options.domainId}`,
        {
          headers: { "apikey": this.options.anonKey, "Authorization": `Bearer ${this.options.anonKey}` },
          signal: AbortSignal.timeout(3000),
        }
      );
      if (!res.ok) return;
      const body = await res.json() as { patterns?: Array<{ pattern: string; category: string }> };
      const valid = new Set<ConsentCategory>(["analytics", "advertising", "functional", "social"]);
      const patterns: BlockPattern[] = [];
      for (const entry of body.patterns ?? []) {
        if (!entry.pattern || !valid.has(entry.category as ConsentCategory)) continue;
        const escaped = entry.pattern.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        patterns.push({ pattern: new RegExp(escaped, "i"), category: entry.category as ConsentCategory });
      }
      this.blocker.addPatterns(patterns);
    } catch {
      // Best effort; static patterns remain in force.
    }
  }

  /**
   * Conservative default used when no published config is readable, because a
   * domain was never configured, was never verified, or the read failed.
   *
   * Deny by default: explicit accept required, no close shortcut, GCM and TCF
   * off, and nothing is released until the visitor decides. The visitor still
   * gets a working consent surface instead of a site with no consent UI.
   */
  private fallbackBannerConfig(): BannerConfig {
    return {
      id: "",
      domain_id: this.options.domainId,
      layout: "bottom_bar",
      primary_color: "#6366f1",
      background_color: "#ffffff",
      text_color: "#111827",
      button_border_radius: "8px",
      logo_url: null,
      font_family: null,
      custom_copy: {},
      show_close_button: false,
      require_explicit_accept: true,
      show_powered_by: true,
      default_regulation: "gdpr",
      categories_enabled: ["strictly_necessary", "analytics", "advertising", "functional"],
      gcm_enabled: false,
      gcm_tag_manager_id: null,
      tcf_enabled: false,
      tcf_cmp_id: null,
      vendor_list_version: null,
      version: 1,
    };
  }

  private async fetchBannerConfig(): Promise<BannerConfig | null> {
    try {
      const res = await fetch(
        `${this.options.apiUrl}/rest/v1/banner_configs?domain_id=eq.${this.options.domainId}&published_at=not.is.null&select=*`,
        {
          headers: { "Content-Type": "application/json", "apikey": this.options.anonKey },
          signal: AbortSignal.timeout(3000),
        }
      );
      if (res.ok) {
        const configs = await res.json() as BannerConfig[];
        return configs[0] ?? null;
      }
    } catch {
      // Silently fail
    }
    return null;
  }

  private handleDecision(decisions: ConsentDecisions, config: BannerConfig): void {
    // Capture the prior state before we overwrite it, to detect a withdrawal
    // (a category that was consented and is now refused).
    const prev = this.currentDecisions;

    // Save to storage
    this.storage.save({
      decisions,
      timestamp: Date.now(),
      bannerVersion: config.version,
      regulation: config.default_regulation,
      sessionId: this.sessionId,
    });

    // Apply decisions (release scripts, update GCM)
    this.applyDecisions(decisions, config);

    // Update TCF API if active
    let tcString: string | undefined;
    if (this.tcfApi) {
      tcString = this.updateTcfConsent(decisions);
    }

    this.currentDecisions = decisions;

    // Record the experiment outcome (only once, for the first decision made on
    // the banner the visitor was shown).
    if (this.activeExperiment) {
      const nonEssential = [decisions.analytics, decisions.advertising, decisions.functional, decisions.social];
      const event = nonEssential.every(Boolean)
        ? "accept"
        : nonEssential.every((v) => !v)
          ? "reject"
          : "custom_accept";
      this.experiments.record(this.activeExperiment.id, this.activeExperiment.variant, event);
      this.activeExperiment = null;
    }

    // Show preference center if not already shown
    if (!this.preferenceCenter && this.currentConfig && this.currentGeo) {
      this.ensurePreferenceCenter(this.currentConfig, this.currentGeo);
    }

    // On withdrawal (a category downgraded from consented to refused), clear
    // that category's known cookies and reload once the record is sent, so
    // already-executed trackers actually stop this session (GDPR Art. 7(3):
    // withdrawal must be as effective as giving consent).
    const cats = ["analytics", "advertising", "functional", "social"] as const;
    const withdrawn = prev ? cats.filter((c) => prev[c] && !decisions[c]) : [];

    // Record to backend (fire-and-forget)
    this.geo.detect().then((geoResult) => {
      this.client.record(this.sessionId, decisions, geoResult, config.version, tcString);
    }).finally(() => {
      if (withdrawn.length > 0) {
        this.clearCategoryCookies(withdrawn);
        window.location.reload();
      }
    });
  }

  /** Best-effort deletion of well-known tracker cookies for withdrawn categories. */
  private clearCategoryCookies(categories: ConsentCategory[]): void {
    const patterns: Record<ConsentCategory, RegExp[]> = {
      analytics: [/^_ga/, /^_gid$/, /^_gat/, /^_gcl_/, /^__utm/, /^_hj/, /^_clck$/, /^_clsk$/, /^mp_/, /^amplitude/, /^ajs_/],
      advertising: [/^_fbp$/, /^_fbc$/, /^fr$/, /^_gcl_au$/, /^IDE$/, /^test_cookie$/, /^MUID$/, /^_uet/, /^personalization_id$/, /^_ttp/, /^_tt_/],
      functional: [/^intercom-/, /^__hstc$/, /^__hssc$/, /^hubspotutk$/, /^drift/],
      social: [/^guest_id/, /^li_/, /^lidc$/, /^bcookie$/, /^_twitter/],
    };
    const host = window.location.hostname;
    const scopes = ["", host, `.${host}`];
    const parts = host.split(".");
    if (parts.length > 2) scopes.push(`.${parts.slice(-2).join(".")}`);

    const names = document.cookie.split(";").map((c) => c.split("=")[0].trim()).filter(Boolean);
    for (const name of names) {
      const matched = categories.some((cat) => patterns[cat].some((re) => re.test(name)));
      if (!matched) continue;
      for (const scope of scopes) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${scope ? `; domain=${scope}` : ""}`;
      }
    }
  }

  private applyDecisions(decisions: ConsentDecisions, config: BannerConfig | null): void {
    // Policy-based enforcement: evaluate each category
    const consented = new Set<ConsentCategory>();
    const regulation = config?.default_regulation ?? "gdpr";
    const cats = ["analytics", "advertising", "functional", "social"] as const;
    for (const cat of cats) {
      const result = this.evaluator.evaluate({ decisions, category: cat, regulation });
      if (result.decision === "allow") consented.add(cat);
    }

    // Keep enforcement live: do not stop() after a decision, so trackers
    // injected later (SPA navigation, late tag managers) stay gated against
    // the recorded consent instead of running unblocked.
    this.blocker.release(consented);

    // Update GCM
    if (this.gcm) {
      this.gcm.update(decisions);
    } else if (config?.gcm_enabled) {
      this.gcm = new GoogleConsentMode(true);
      this.gcm.update(decisions);
    }

    // Dispatch DOM event for custom integrations
    window.dispatchEvent(new CustomEvent("pb:consent", { detail: decisions }));
  }

  showPreferences(): void {
    if (!this.preferenceCenter && this.currentConfig && this.currentGeo) {
      this.ensurePreferenceCenter(this.currentConfig, this.currentGeo);
    }
    const pc: PreferenceCenter | null = this.preferenceCenter;
    pc?.openPreferences();
  }

  private ensurePreferenceCenter(config: BannerConfig, geoResult: GeoResult): void {
    if (this.preferenceCenter) return;
    this.preferenceCenter = new PreferenceCenter(
      config,
      geoResult,
      () => this.currentDecisions ?? { strictly_necessary: true, analytics: false, advertising: false, functional: false, social: false },
      (newDecisions) => this.handleDecision(newDecisions, config),
      this.options.lang || navigator.language.split("-")[0] || "en",
      this.vendorList,
      () => {
        const stored = this.storage.load();
        return stored ? { timestamp: stored.timestamp, sessionId: stored.sessionId } : null;
      }
    );
    this.preferenceCenter.show();
  }

  /**
   * Session-scoped "banner dismissed" flag. Deliberately NOT consent storage:
   * no consent record is created, nothing is sent to the backend, and the
   * banner re-prompts in the next browser session.
   */
  private softDismissKey(): string {
    return `pb_dismissed_${this.options.domainId}`;
  }

  private isSoftDismissed(): boolean {
    try {
      return sessionStorage.getItem(this.softDismissKey()) === "1";
    } catch {
      return false;
    }
  }

  private markSoftDismissed(): void {
    try {
      sessionStorage.setItem(this.softDismissKey(), "1");
    } catch {
      // Storage unavailable: worst case the banner re-shows on next page load.
    }
  }

  private reportConfigurationError(message: string): void {
    console.warn(`[Pritect Beacon] ${message}`);
    window.dispatchEvent(new CustomEvent("pb:error", { detail: { message } }));
  }

  // ─── TCF Integration ──────────────────────────────────────────────

  /**
   * Initialize TCF API and vendor list loader.
   * @param hasExistingConsent Whether consent was already stored
   * @param existingDecisions Stored decisions (if any) to push to TCF API
   * @param geo Geo result for publisher country code
   */
  private initTcf(
    hasExistingConsent: boolean,
    existingDecisions: ConsentDecisions | null,
    geo: GeoResult,
    cmpId: number
  ): void {
    this.tcfApi = new TcfApi({
      cmpId,
      cmpVersion: 1,
      gdprApplies: true,
    });
    this.tcfApi.install(hasExistingConsent);

    // Load vendor list in the background (non-blocking)
    this.vendorListLoader = new VendorListLoader();
    this.vendorListLoader.load().then((vl) => {
      this.vendorList = vl;
      // If we already had consent, update TCF with vendor data now
      if (hasExistingConsent && existingDecisions) {
        this.updateTcfConsent(existingDecisions, false);
      }
    });

    // If existing consent, immediately push what we can (without vendor list)
    if (hasExistingConsent && existingDecisions) {
      this.updateTcfConsent(existingDecisions);
    }
  }

  /**
   * Map Pritect consent categories to TCF purposes and update the TCF API.
   * Returns the TC string.
   */
  private updateTcfConsent(decisions: ConsentDecisions, isUserAction: boolean = true): string {
    if (!this.tcfApi) return "";

    const purposeConsents = new Set<number>();
    const purposeLI = new Set<number>();
    const specialFeatures = new Set<number>();

    // Purpose 1 (store/access information on a device) requires consent under
    // ePrivacy Art. 5(3); it is NOT strictly necessary. Grant it only when the
    // user consented to at least one purpose that relies on device storage, so
    // a Reject All produces a TC string that grants nothing.
    if (decisions.analytics || decisions.advertising || decisions.functional || decisions.social) {
      purposeConsents.add(1);
    }

    // Analytics → Purposes 7, 8, 9
    if (decisions.analytics) {
      purposeConsents.add(7);
      purposeConsents.add(8);
      purposeConsents.add(9);
    }

    // Advertising → Purposes 2, 3, 4, 5, 6, 10
    if (decisions.advertising) {
      purposeConsents.add(2);
      purposeConsents.add(3);
      purposeConsents.add(4);
      purposeConsents.add(5);
      purposeConsents.add(6);
      purposeConsents.add(10);
    }

    // Functional → Purpose 1 (already added) + Special Feature 1 (precise geolocation)
    if (decisions.functional) {
      specialFeatures.add(1);
    }

    // Social → Purposes 3, 4, 5
    if (decisions.social) {
      purposeConsents.add(3);
      purposeConsents.add(4);
      purposeConsents.add(5);
    }

    // Determine vendor consents from vendor list
    const vendorConsents = new Set<number>();
    const vendorLI = new Set<number>();

    if (this.vendorList) {
      for (const vendorId of Object.keys(this.vendorList.vendors)) {
        const vendor = this.vendorList.vendors[vendorId];
        if (!vendor) continue;

        // Consent: vendor is consented if ALL its declared purposes are consented
        const allPurposesConsented = vendor.purposes.every((pid: number) => purposeConsents.has(pid));
        if (allPurposesConsented && vendor.purposes.length > 0) {
          vendorConsents.add(vendor.id);
        }

        // Legitimate interest: vendor LI if all its legInt purposes are satisfied
        const allLIPurposes = vendor.legIntPurposes.every((pid: number) =>
          purposeConsents.has(pid) || purposeLI.has(pid)
        );
        if (allLIPurposes && vendor.legIntPurposes.length > 0) {
          vendorLI.add(vendor.id);
        }
      }
    }

    const geo = this.currentGeo;
    const publisherCC = geo?.country?.toUpperCase() || "AA";
    const consentLanguage = (this.options.lang || navigator.language.split("-")[0] || "en").toUpperCase().substring(0, 2);
    const vendorListVersion = this.vendorList?.vendorListVersion ?? 0;

    this.tcfApi.updateConsent({
      purposeConsents,
      purposeLegitimateInterests: purposeLI,
      vendorConsents,
      vendorLegitimateInterests: vendorLI,
      specialFeatureOptins: specialFeatures,
      vendorListVersion,
      consentLanguage,
      publisherCC,
    }, isUserAction);

    return this.tcfApi.getTcString();
  }
}
